import { Clock3 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useMobile } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import { WhatIsAdvisory } from "@krea/customer-features/shared-components/advice/what-is-advisory";
import { YourAdvisor } from "@krea/customer-features/shared-components/advice/your-advisor";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import Button from "@krea/customer-features/shared-components/button";
import StepperVertical from "@krea/customer-features/shared-components/stepper-vertical";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { AdviceCategoryEnum } from "@krea/customer-features/types/loanApplication";
import {
  googleCalendarUrlFI,
  googleCalendarUrlSE,
} from "@krea/shared/constants";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";
import { CountryCode } from "@krea/shared/types/common";

const CALENDAR_URL_MAP: Record<CountryCode, string> = {
  [CountryCode.SE]: googleCalendarUrlSE,
  [CountryCode.FI]: googleCalendarUrlFI,
};

interface IDigitalAdvicePageProps {
  onSendApplicationClick: () => void;
}

export const ManualAdvice = ({
  onSendApplicationClick,
}: IDigitalAdvicePageProps) => {
  const isMobile = useMobile();
  const { t } = useTranslation();
  const { data } = useGetCachedCurrentLoanApplication();

  const { adviceCategory } = data ?? {};

  const shouldShowSubmitButton = adviceCategory === AdviceCategoryEnum.SIMPLE;

  const BookAdvisorButton = () => {
    const onBookAdvisorClick = () => {
      window.open(CALENDAR_URL_MAP[settings.countryCode], "_blank");
    };

    return (
      <Button
        block
        size="lg"
        tabIndex={0}
        className={"tw-mt-6 tw-w-full tw-text-center"}
        onClick={onBookAdvisorClick}
      >
        {t("advice.manual.bookAdvisory")}
      </Button>
    );
  };

  return (
    <LayoutOneColumn
      aside={
        <div
          className={twMerge({
            ["tw-w-full tw-px-6 tw-bg-white"]: isMobile,
          })}
          style={
            isMobile
              ? {
                  zIndex: "100000",
                  position: "fixed",
                  left: "0",
                  bottom: "0",
                  height: "100px",
                  boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.10)",
                }
              : { display: "none" }
          }
        >
          <BookAdvisorButton />
        </div>
      }
    >
      <h2 className="tw-my-5 tw-text-4xl tw-font-semibold">
        {t("advice.manual.heading")}
      </h2>
      <div
        className={twMerge(
          "tw-border tw-border-solid tw-border-mint-500 tw-rounded-xl tw-mb-7",
        )}
      >
        <YourAdvisor
          className="tw-rounded-t-xl"
          title={
            <h4 className="tw-text-base tw-font-semibold">
              {t("advice.manual.subheading")}
            </h4>
          }
        />

        <div className="tw-px-5 tw-pt-4">
          <WhatIsAdvisory />

          <StepperVertical
            className="tw-mx-5 tw-mt-4 [&_li[data-active=true]]:before:-tw-ml-[3px] [&_li[data-active=true]]:before:tw-h-3 [&_li[data-active=true]]:before:tw-w-3 [&_li[data-active=true]]:before:tw-border [&_li]:before:tw-border-mint-500"
            steps={[
              { title: t("advice.manual.stepsList.step1"), active: true },
              { title: t("advice.manual.stepsList.step2") },
              { title: t("advice.manual.stepsList.step3") },
              { title: t("advice.manual.stepsList.step4") },
            ]}
          />
        </div>
        <div
          className={twMerge(
            "tw-rounded-b-xl tw-py-4 tw-px-5 tw-bg-mint-100 tw-flex tw-items-center tw-mt-4 tw-gap-3",
          )}
        >
          <div className="tw-h-5  tw-w-5">
            <Clock3
              strokeWidth={1.5}
              className=" tw-fill-mint-200 [&_circle]:tw-stroke-mint-100 [&_polyline]:tw-stroke-mint-500"
            />
          </div>
          <p className="tw-m-0 tw-text-sm tw-font-semibold">
            {t("advice.manual.footer")}
          </p>
        </div>
      </div>
      {shouldShowSubmitButton && (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-5 tw-bg-primary-150 tw-px-4 tw-py-3">
          <h5 className="tw-m-0 tw-text-base tw-font-semibold">
            {t("advice.manual.submitApplication.title")}
          </h5>
          <Button variant="text" onClick={onSendApplicationClick}>
            {t("advice.manual.submitApplication.cta")}
          </Button>
        </div>
      )}

      {!isMobile ? <BookAdvisorButton /> : <div className="tw-h-20" />}
    </LayoutOneColumn>
  );
};
